/**
 * Events Service - Reads event data from Hostinger /events/ folder using index.json
 * This service fetches event data directly from the server using a master index
 */

import { CacheManager } from '../utils/cacheManager';
import { normalizeArrayData } from '../utils/dataFormatHandler';
import safeStorage from '../utils/safeStorage';
import { STORAGE_KEYS } from '../utils/storageKeys';

class EventsService {
  constructor() {
    this.baseUrl = window.location.origin;
    this.cacheManager = new CacheManager(5 * 60 * 1000); // 5 minutes
  }

  /**
   * Get all events from the master /events/index.json file
   * @returns {Promise<Array>} Array of event objects
   */
  async getEvents() {
    // Check cache first
    const cachedEvents = this.cacheManager.get();
    if (cachedEvents) {
      return cachedEvents;
    }

    try {
      console.log('🌐 Fetching events from master index...');
      
      const response = await fetch(`${this.baseUrl}/events/index.json`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Events loaded from master index:', data);
      
      // Handle both array format and object format using shared utility
      const events = normalizeArrayData(data, 'events', 'events');
      
      console.log('📊 Events data processed:', {
        eventsCount: events.length,
        events: events.map(e => ({ id: e.id, name: e.name, folder: e.folder }))
      });
      
      // Cache the results
      this.cacheManager.set(events);
      
      return events;
    } catch (error) {
      console.error('❌ Error fetching events from index:', error);
      
      // Fallback to localStorage if server fetch fails
      const fallbackEvents = this.getEventsFromLocalStorage();
      if (fallbackEvents.length > 0) {
        console.log('📱 Using localStorage fallback for events');
        return fallbackEvents;
      }
      
      return [];
    }
  }

  /**
   * Get a specific event by ID from its individual index.json
   * @param {string} eventId - The event ID (folder name)
   * @returns {Promise<Object|null>} Event data or null if not found
   */
  async getEvent(eventId) {
    try {
      console.log(`🔍 Fetching event data for: ${eventId}`);
      
      const response = await fetch(`${this.baseUrl}/events/${eventId}/event.json`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const eventData = await response.json();
      console.log('✅ Event data loaded:', eventData);
      
      return eventData;
    } catch (error) {
      console.error(`❌ Error fetching event ${eventId}:`, error);
      return null;
    }
  }

  /**
   * Get events from localStorage (fallback)
   */
  getEventsFromLocalStorage() {
    return safeStorage.getJSON(STORAGE_KEYS.EVENTS, []);
  }

  /**
   * Save events to localStorage (for offline access)
   */
  saveEventsToLocalStorage(events) {
    if (safeStorage.setJSON(STORAGE_KEYS.EVENTS, events)) {
      console.log('💾 Events saved to localStorage');
    }
  }

  /**
   * Clear cache (useful for testing or when you know data has changed)
   */
  clearCache() {
    this.cacheManager.clear();
    console.log('🗑️ Events cache cleared');
  }
}

// Export a singleton instance
const eventsServiceInstance = new EventsService();
export default eventsServiceInstance;